<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-plus</v-icon>
        Add User
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- Personal Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Personal Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newUser.firstName"
                label="First Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'First name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newUser.surname"
                label="Surname"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-outline"
                :rules="[v => !!v || 'Surname is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Contact Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Contact Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newUser.email"
                label="Email Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[
                  v => !!v || 'Email is required',
                  v => /.+@.+\..+/.test(v) || 'Must be a valid email'
                ]"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newUser.phone"
                label="Phone Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                :rules="phoneValidationRules"
                @input="formatPhoneInput"
                placeholder="074 551 1155"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Role & Permissions -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Role & Permissions</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="newUser.role"
                :items="userRoles"
                label="User Role"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-tie"
                :rules="[v => !!v || 'User role is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="newUser.department"
                :items="departments"
                label="Department"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-office-building"
              />
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                :disabled="!valid || loading"
                color="#002060"
                dark
                large
                rounded
                @click="addUser"
                class="mr-4"
              >
                <v-icon left>mdi-account-plus</v-icon>
                Add User
              </v-btn>
              
              <v-btn
                outlined
                color="#002060"
                large
                rounded
                @click="$router.back()"
              >
                <v-icon left>mdi-close</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db, app, firebaseConfig } from '../firebaseConfig';
import { doc, setDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, signOut, fetchSignInMethodsForEmail } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { ActivityLogger } from '../utils/activityLogger';
import { generateEmployeeId } from '../utils/employeeIdGenerator';
import { generateTemporaryPassword } from '../utils/passwordGenerator';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';

export default {
  name: 'AddUser',
  components: {
    NotificationDialog
  },
  data() {
    return {
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      newUser: {
        firstName: '',
        surname: '',
        email: '',
        phone: '',
        role: '',
        department: 'Legal',
        userType: 'member'
      },
      temporaryPassword: '',
      userRoles: ['Admin', 'Executive', 'Employee', 'QA Agent'],
      departments: ['Legal', 'HR', 'Finance', 'IT', 'Operations'],
      secondaryApp: null,
      secondaryAuth: null
    };
  },
  computed: {
    ...mapGetters(['user']),
    phoneValidationRules() {
      return getPhoneValidationRules();
    }
  },
  methods: {

    formatPhoneInput() {
      this.newUser.phone = formatPhoneInput(this.newUser.phone);
    },

    normalizeEmailInput(email) {
      const trimmedEmail = (email || '').trim();
      return {
        trimmedEmail,
        normalizedEmail: trimmedEmail.toLowerCase()
      };
    },

    async getExistingEmailRecords(normalizedEmail, trimmedEmail) {
      if (!trimmedEmail) {
        return [];
      }

      try {
        const usersRef = collection(db, 'users');
        const lookupValues = Array.from(new Set([normalizedEmail, trimmedEmail])).filter(Boolean);
        const queries = [];

        if (lookupValues.length === 1) {
          queries.push(getDocs(query(usersRef, where('email', '==', lookupValues[0]))));
        } else if (lookupValues.length > 1) {
          queries.push(getDocs(query(usersRef, where('email', 'in', lookupValues))));
        }

        queries.push(getDocs(query(usersRef, where('emailNormalized', '==', normalizedEmail))));

        const snapshots = await Promise.all(queries);
        const seen = new Set();
        const records = [];

        snapshots.forEach(snapshot => {
          snapshot.forEach(docSnap => {
            if (!seen.has(docSnap.id)) {
              seen.add(docSnap.id);
              records.push({ id: docSnap.id, ...docSnap.data() });
            }
          });
        });

        return records;
      } catch (error) {
        console.error('Error while fetching existing email records:', error);
        return [];
      }
    },

    async checkEmailAvailability(email) {
      const { trimmedEmail, normalizedEmail } = this.normalizeEmailInput(email);

      if (!trimmedEmail) {
        return {
          trimmedEmail,
          normalizedEmail,
          existingRecords: [],
          authExists: false
        };
      }

      const existingRecords = await this.getExistingEmailRecords(normalizedEmail, trimmedEmail);
      let authExists = false;

      try {
        this.initializeSecondaryApp();
        const methods = await fetchSignInMethodsForEmail(this.secondaryAuth, trimmedEmail);
        authExists = Array.isArray(methods) && methods.length > 0;
      } catch (error) {
        console.error('Error checking Firebase Auth for existing email:', error);
      }

      return {
        trimmedEmail,
        normalizedEmail,
        existingRecords,
        authExists
      };
    },

    initializeSecondaryApp() {
      if (!this.secondaryApp) {
        // Use the same Firebase project configuration as the primary app
        this.secondaryApp = initializeApp({ ...firebaseConfig }, 'secondary');
        this.secondaryAuth = getAuth(this.secondaryApp);
      }
    },

    async addUser() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      this.showNotification = false;
      try {
        const { existingRecords, authExists, trimmedEmail, normalizedEmail } = await this.checkEmailAvailability(this.newUser.email);

        const activeRecord = existingRecords.find(record => (record.status || 'active').toLowerCase() === 'active');
        if (activeRecord) {
          const fullName = [activeRecord.firstName, activeRecord.surname].filter(Boolean).join(' ') || 'an existing user';
          this.notificationType = 'error';
          this.notificationTitle = 'Email Already Exists!';
          this.notificationMessage = `This email is already assigned to ${fullName}. Please use a different email address or update the existing user.`;
          this.showNotification = true;
          this.loading = false;
          return;
        }

        const staleRecords = existingRecords.filter(record => (record.status || '').toLowerCase() !== 'active');

        if (staleRecords.length && authExists) {
          this.notificationType = 'error';
          this.notificationTitle = 'Email Already Exists!';
          this.notificationMessage = 'This email already has a Firebase Auth account. Reactivate the existing user from the Users page or contact support.';
          this.showNotification = true;
          this.loading = false;
          return;
        }

        if (staleRecords.length) {
          try {
            await Promise.all(
              staleRecords.map(record => deleteDoc(doc(db, 'users', record.id)))
            );
            console.log(`Removed ${staleRecords.length} stale user record(s) before creating a new account for ${trimmedEmail}.`);
          } catch (cleanupError) {
            console.error('Failed to remove stale user records:', cleanupError);
          }
        }

        // Normalize email before proceeding
        this.newUser.email = trimmedEmail;

        // Initialize secondary app for user creation
        this.initializeSecondaryApp();
        
        // Generate temporary password if not already generated
        if (!this.temporaryPassword) {
          this.temporaryPassword = generateTemporaryPassword();
        }
        
        // Create Firebase Auth account using secondary auth instance
        const userCredential = await createUserWithEmailAndPassword(
          this.secondaryAuth, 
          trimmedEmail, 
          this.temporaryPassword
        );
        
        const user = userCredential.user;
        
        // Update the user's display name
        await updateProfile(user, {
          displayName: `${this.newUser.firstName} ${this.newUser.surname}`
        });

        // Generate a unique employee ID
        const employeeId = generateEmployeeId();
        
        // Create user data object
        const userData = {
          firstName: this.newUser.firstName,
          surname: this.newUser.surname,
          email: trimmedEmail,
          emailNormalized: normalizedEmail,
          phone: this.newUser.phone || '',
          employeeId: employeeId,
          role: this.newUser.role,
          userType: this.newUser.userType || 'member',
          department: this.newUser.department,
          status: 'active',
          hasTemporaryPassword: true,
          temporaryPassword: this.temporaryPassword,
          dateAdded: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          createdAt: new Date().toISOString(),
          uid: user.uid
        };
        
        // Use the auth UID as the document ID
        await setDoc(doc(db, 'users', user.uid), userData);
        console.log('User added with Auth ID:', user.uid);
        
        // Sign out the newly created user from the secondary auth instance
        await signOut(this.secondaryAuth);
        
        // Log user creation activity
        const createdBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.userCreated(createdBy, this.user?.uid, `${userData.firstName} ${userData.surname}`);
        
        // Send email with temporary password
        try {
          const response = await fetch(
            'https://us-central1-dmm-law-user-portal.cloudfunctions.net/sendUserCreationEmail',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: userData.email,
                firstName: userData.firstName,
                surname: userData.surname,
                temporaryPassword: this.temporaryPassword,
                employeeId: userData.employeeId,
                role: userData.role,
                department: userData.department
              })
            }
          );
          
          if (response.ok) {
            const result = await response.json();
            console.log('User creation email sent successfully:', result);
          } else {
            const errorText = await response.text();
            console.error('Failed to send user creation email:', response.status, errorText);
          }
        } catch (emailError) {
          console.error('Error sending user creation email:', emailError);
          // Don't fail the user creation if email fails
        }
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'User Added Successfully!';
        this.notificationMessage = 'User created successfully! An email has been sent to the user with their login details.';
        this.showNotification = true;
        
      } catch (error) {
        console.error('Error adding user:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        
        if (error.code === 'auth/email-already-in-use') {
          this.notificationMessage = 'This email is already registered in Firebase Auth. The user may exist but not in the users collection. Please check the Firebase Console or contact support.';
        } else if (error.code === 'auth/weak-password') {
          this.notificationMessage = 'Password is too weak. Please use a stronger password.';
        } else if (error.code === 'auth/invalid-email') {
          this.notificationMessage = 'Please enter a valid email address.';
        } else if (error.code === 'auth/operation-not-allowed') {
          this.notificationMessage = 'Email/password accounts are not enabled. Please contact support.';
        } else {
          this.notificationMessage = `Failed to add user: ${error.message || 'Unknown error'}. Please try again.`;
        }
        
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },
    handleNotificationClose() {
      this.showNotification = false;
      if (this.notificationType === 'success') {
        // Reset form and go back after successful addition
        this.resetForm();
        this.$router.back();
      }
    },
    
    resetForm() {
      this.newUser = {
        firstName: '',
        surname: '',
        email: '',
        phone: '',
        role: '',
        department: 'Legal',
        userType: 'member'
      };
      this.temporaryPassword = '';

      this.$refs.form.resetValidation();
    }
  },



  beforeUnmount() {
    // Clean up secondary app instance
    if (this.secondaryApp) {
      try {
        this.secondaryApp.delete();
      } catch (error) {
        console.log('Error cleaning up secondary app:', error);
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 
